// src/components/admin/ChangeRoleModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { BsX, BsPersonGear } from "react-icons/bs";
import { useMemberStore } from "../../store/memberStore";
import useAuthStore from "../../store/authStore";
import { assignableRoles, roleLabel } from "../../utils/permissions";

// Ce que change chaque rôle, affiché avant validation
const ROLE_HINTS = {
  superadmin: "Tous les droits, y compris sur les autres administrateurs.",
  admin: "Gestion complète du salon, des rôles et des comptes.",
  manager: "Gestion du salon (rendez-vous, ventes, catalogue, équipe), sans changer les rôles.",
  financier: "Espace admin en lecture : ventes, rapports, catalogue et stocks.",
  employee: "Espace admin en lecture et assignation aux rendez-vous.",
  client: "Plus d'accès à l'espace admin : uniquement l'espace client.",
};

const ChangeRoleModal = ({ isOpen, onClose, member, onChanged = () => {} }) => {
  const { user } = useAuthStore();
  const { roles, getRoles, updateUserRole, actionLoading } = useMemberStore();
  const [selectedRole, setSelectedRole] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSelectedRole("");
      if (!roles?.length) getRoles();
    }
  }, [isOpen]);

  const options = useMemo(
    () => assignableRoles(user, member, roles || []),
    [user, member, roles],
  );

  if (!isOpen || !member) return null;

  const currentRole = member.role?.name;
  const leavesEmployee = currentRole === "employee" && selectedRole && selectedRole !== "employee";

  const handleSubmit = async () => {
    if (!selectedRole) return;
    try {
      await updateUserRole(member.id, selectedRole);
      onChanged(selectedRole);
      onClose();
    } catch (error) {
      // Le message d'erreur est déjà affiché par le store
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              Changer le rôle
            </h2>
            <p className="font-nunito text-[14px] text-text mt-1">
              {member.prenom} {member.nom} · rôle actuel :{" "}
              <strong>{roleLabel(currentRole)}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 transition-colors"
            disabled={actionLoading}
          >
            <BsX size={24} />
          </button>
        </div>

        <div className="p-6 space-y-3">
          <div className="flex items-center justify-center mb-2">
            <div className="p-3 bg-blue-50">
              <BsPersonGear size={32} className="text-blue-600" />
            </div>
          </div>

          {options.length === 0 ? (
            <p className="font-nunito text-[14px] text-text text-center">
              Aucun autre rôle ne peut être attribué à ce compte.
            </p>
          ) : (
            options.map((role) => (
              <label
                key={role.id}
                className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                  selectedRole === role.name
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="role"
                  value={role.name}
                  checked={selectedRole === role.name}
                  onChange={() => setSelectedRole(role.name)}
                  className="mt-1"
                />
                <span>
                  <span className="block font-poppins font-medium text-[15px] text-subtitle">
                    {roleLabel(role.name)}
                  </span>
                  <span className="block font-nunito text-[13px] text-text">
                    {ROLE_HINTS[role.name] || ""}
                  </span>
                </span>
              </label>
            ))
          )}

          {leavesEmployee && (
            <p className="font-nunito text-[13px] text-orange-700 bg-orange-50 border border-orange-200 px-3 py-2">
              Cet employé sera retiré des rendez-vous à venir auxquels il est
              assigné.
            </p>
          )}

          <p className="font-nunito text-[13px] text-text">
            Le changement s'applique immédiatement, sans que la personne ait à se
            reconnecter.
          </p>
        </div>

        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 font-nunito text-[14px] font-semibold hover:bg-gray-50 transition-colors"
            disabled={actionLoading}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedRole || actionLoading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white font-nunito text-[14px] font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {actionLoading ? "Enregistrement..." : "Changer le rôle"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeRoleModal;
